import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { connect } from "react-redux";
import { checkUserAuthenticated } from "../Redux/actions/user.js";
import HomeMap from "./Map/HomeMap.js";
import CompanyMap from "./Map/CompanyMap.js";
import MeetMenu from "./Meet/Menu.js";
import MeetAdmin from "./Meet/Admin.js";
import MeetViewer from "./Meet/Viewer.js";
import CanvasEditor from "./Canvas/Editor.js";
import NotFound from "./Errors/404.js";
import WebGLError from "./Errors/WebGLError.js";
import SiteControls from "./Common/SiteControls.js";
import InventumPage from "./InventumViewer/InventumPage.js";
import PasswordResetContainer from "./Common/Popups/PasswordReset.js";
import StagingAuthWall from "./StagingAuthWall.js";
import withRouter from "../withRouter.js";
import { API_BASE_URL } from "../axios-config.js";

let sass = require("./scss/_main.scss");

const CompanyMapWrapper = withRouter(({ match }) => {
  const { companySlug, companyID } = match.params;
  return (
    <SiteControls>
      <CompanyMap companySlug={companySlug} companyID={companyID} />
    </SiteControls>
  );
});

const InventumPageWrapper = withRouter(({ match }) => {
  const { companySlug, companyID, projectName } = match.params;
  return (
    <SiteControls>
      <InventumPage companySlug={companySlug} companyID={companyID} projectName={projectName} />
    </SiteControls>
  );
});

const MeetViewerWrapper = withRouter(({ match }) => {
  const { meetCode } = match.params;
  return (
    <SiteControls>
      <MeetViewer meetCode={meetCode} />
    </SiteControls>
  );
});

const PasswordResetWrapper = withRouter(({ match }) => {
  const { resetToken } = match.params;
  return (
    <SiteControls>
      <PasswordResetContainer token={resetToken} />
    </SiteControls>
  );
});

const HomeMapWrapper = () => (
  <SiteControls>
    <HomeMap />
  </SiteControls>
);

const CanvasEditorWrapper = () => (
  <SiteControls>
    <CanvasEditor />
  </SiteControls>
);

const App = ({ checkUserAuthenticated }) => {
  const parseJWT = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  };

  const getGPUInfo = () => {
    const gl = document.createElement("canvas").getContext("webgl");
    if (!gl) {
      return {
        error: "No WebGL",
      };
    }
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    return debugInfo
      ? {
          vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
          renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
        }
      : {
          error: "No WebGL Info",
        };
  };

  const handleError = (msg, src, lineno, colno, error) => {
    const tokenPayload = parseJWT(window.localStorage.getItem("inventumToken"));
    const errReport = {
      user: {
        id: tokenPayload ? tokenPayload.id : null,
      },
      error: {
        message: msg,
        source: src,
        lineNumber: lineno,
        columnNumber: colno,
        stack: error?.stack || null,
      },
      page: {
        href: window.location.href,
      },
      device: {
        userAgent: window.navigator.userAgent,
        threads: window.navigator.hardwareConcurrency,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        platform: window.navigator.userAgent,
        gpuInfo: getGPUInfo(),
      },
      timestamp: Date.now(),
    };

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/logerror`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.send(JSON.stringify({ report: errReport }));
  };

  useEffect(() => {
    checkUserAuthenticated();
    window.onerror = handleError;
    return () => {
      window.onerror = null;
    };
  }, [checkUserAuthenticated]);

  return (
    <StagingAuthWall>
      <Routes>
        <Route path="/" element={<HomeMapWrapper />} />
        <Route path="/canvas" element={<CanvasEditorWrapper />} />
        <Route path="/app" element={<HomeMapWrapper />} />
        <Route path="/register" element={<HomeMapWrapper />} />
        <Route path="/resetpassword" element={<HomeMapWrapper />} />
        <Route path="/resetpassword/:resetToken" element={<PasswordResetWrapper />} />
        <Route path="/c/:companySlug" element={<CompanyMapWrapper />} />
        <Route path="/g/:companyID" element={<CompanyMapWrapper />} />
        <Route path="/c/:companySlug/:projectName" element={<InventumPageWrapper />} />
        <Route path="/g/:companyID/:projectName" element={<InventumPageWrapper />} />
        <Route path="/c/:companySlug/:projectName/review" element={<InventumPageWrapper />} />
        <Route path="/g/:companyID/:projectName/review" element={<InventumPageWrapper />} />
        <Route path="/meet" element={<MeetMenu />} />
        <Route path="/meet/admin" element={<MeetAdmin />} />
        <Route path="/meet/:meetCode" element={<MeetViewerWrapper />} />
        <Route path="/webglnotsupported" element={<WebGLError />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </StagingAuthWall>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  checkUserAuthenticated: () => {
    dispatch(checkUserAuthenticated());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
